import React from "react";
import {
  FaMapMarkerAlt,
  FaHome,
  FaBuilding,
  FaChartLine
} from "react-icons/fa";
import { formatDate } from "utils/helperFunctions";

export default function SuburbView({ suburb }) {
  if (!suburb) return null;

  const formatPrice = (price) =>
    price != null ? `$${Number(price).toLocaleString()}` : "N/A";

  const formatGrowth = (growth) => {
    if (!growth || growth === "N/A")
      return <span className="text-darkgray">N/A</span>;
    const num = parseFloat(growth);
    return (
      <span
        className={
          num >= 0 ? "text-green-600 font-medium" : "text-red-600 font-medium"
        }
      >
        {num >= 0 ? "+" : ""}
        {growth}
      </span>
    );
  };

  const StatTable = ({ stats, label }) => {
    if (!stats || stats.length === 0)
      return (
        <p className="text-sm text-darkgray italic">
          No {label} data available.
        </p>
      );

    return (
      <>
        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mediumgray">
                {[
                  "Year",
                  "Median Price",
                  "Sales Volume",
                  "Growth",
                  "High Demand"
                ].map((h) => (
                  <th
                    key={h}
                    className="pb-2 text-left text-xs font-medium text-darkgray"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-mediumgray">
              {stats.map((s, i) => (
                <tr key={i}>
                  <td className="py-2 font-medium">{s.year}</td>
                  <td className="py-2">{formatPrice(s.medianSalePrice)}</td>
                  <td className="py-2">{s.annualSalesVolume ?? "N/A"}</td>
                  <td className="py-2">{formatGrowth(s.suburbGrowth)}</td>
                  <td className="py-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        s.highDemandArea === "Yes"
                          ? "bg-green-100 text-green-800"
                          : "bg-lightgray text-darkgray"
                      }`}
                    >
                      {s.highDemandArea ?? "N/A"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="sm:hidden space-y-3">
          {stats.map((s, i) => (
            <div
              key={i}
              className="border border-mediumgray rounded-lg p-3 bg-white space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{s.year}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    s.highDemandArea === "Yes"
                      ? "bg-green-100 text-green-800"
                      : "bg-lightgray text-darkgray"
                  }`}
                >
                  {s.highDemandArea === "Yes" ? "High Demand" : "Normal"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-xs text-darkgray">Median Price</p>
                  <p className="font-medium">
                    {formatPrice(s.medianSalePrice)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-darkgray">Sales Volume</p>
                  <p className="font-medium">{s.annualSalesVolume ?? "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs text-darkgray">Growth</p>
                  <p>{formatGrowth(s.suburbGrowth)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-lg mb-6">
        <div className="flex items-center gap-2">
          <FaMapMarkerAlt className="text-darkgray flex-shrink-0" size={15} />
          <h4 className="m-0 text-lg sm:text-xl">{suburb.suburb}</h4>
        </div>
        <div className="mt-1 ml-6 text-darkgray text-sm flex flex-wrap gap-x-3 gap-y-1">
          <span>
            {suburb.state} &bull; {suburb.postcode}
          </span>
          {suburb.location?.coordinates && (
            <span className="text-xs text-darkgray">
              {suburb.location.coordinates[1].toFixed(5)},{" "}
              {suburb.location.coordinates[0].toFixed(5)}
            </span>
          )}
        </div>
      </div>

      <div className="border-t border-mediumgray">
        <dl className="divide-y divide-mediumgray">
          {/* REA Price */}
          <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium flex items-center gap-2 mb-1 sm:mb-0">
              <FaHome className="text-darkgray" size={13} />
              REA Price
            </dt>
            <dd className="text-sm sm:col-span-2">
              {suburb.reaPrice ? (
                <span className="font-semibold">${suburb.reaPrice}</span>
              ) : (
                <span className="text-darkgray italic">Not set</span>
              )}
            </dd>
          </div>

          {/* Domain Price Tiers */}
          <div className="bg-lightergray px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium flex items-center gap-2 mb-2 sm:mb-0">
              <FaBuilding className="text-darkgray" size={13} />
              Domain Price Tiers
            </dt>
            <dd className="text-sm sm:col-span-2">
              {suburb.domainPrice?.length > 0 ? (
                <div className="space-y-2">
                  {suburb.domainPrice.map((tier, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-white border border-mediumgray rounded-lg px-3 py-2"
                    >
                      <span className="text-darkgray text-xs">
                        {formatPrice(tier.minPrice)} –{" "}
                        {formatPrice(tier.maxPrice)}
                      </span>
                      <span className="font-semibold text-sm">
                        {formatPrice(tier.fee)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-darkgray italic">Not set</span>
              )}
            </dd>
          </div>

          {/* Description */}
          {suburb.description && (
            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium flex items-center gap-2 mb-2 sm:mb-0">
                <FaChartLine className="text-darkgray" size={13} />
                Suburb Profile
              </dt>
              <dd className="text-sm sm:col-span-2">
                <pre className="whitespace-pre-wrap font-sans text-darkgray leading-relaxed max-h-48 overflow-y-auto text-xs">
                  {suburb.description}
                </pre>
              </dd>
            </div>
          )}

          {/* House Stats */}
          <div className="bg-lightergray px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium flex items-center gap-2 mb-3 sm:mb-0">
              <FaHome className="text-darkgray" size={13} />
              House Stats
            </dt>
            <dd className="text-sm sm:col-span-2">
              <StatTable stats={suburb.houseStats} label="house" />
            </dd>
          </div>

          {/* Unit Stats */}
          <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium flex items-center gap-2 mb-3 sm:mb-0">
              <FaBuilding className="text-darkgray" size={13} />
              Unit Stats
            </dt>
            <dd className="text-sm sm:col-span-2">
              <StatTable stats={suburb.unitStats} label="unit" />
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
